import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { formatBudgetHours, formatEur } from './budget';
import type { Deviation } from './insights';

/** One height for every cell: two `height` utilities are settled by emit order, not by listing. */
const planCell = 'h-12 border-b border-r border-border px-3 max-md:h-10 max-md:px-2';

/** Compare's colours: past the plan is red, time left over green. */
const gapClass = (amount: number | null): string => {
  if (amount === null || amount === 0) return 'text-muted-foreground';
  return amount > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400';
};

/** Signed both ways: the minus comes with the number, so time over plan takes a plus. */
const signedEur = (amount: number): string => `${amount > 0 ? '+' : ''}${formatEur(amount)}`;
const signedHours = (amount: number): string => `${amount > 0 ? '+' : ''}${formatBudgetHours(amount)}`;

/** Neutral ink, never red: red means past the plan here, and a red first row read as a verdict. */
const RankChip = ({ rank }: { rank: number }): JSX.Element => (
  <span
    className={cx(
      'flex size-7 items-center justify-center rounded-lg text-sm font-semibold tabular-nums max-md:size-6 max-md:text-xs',
      rank === 1 ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground',
    )}
  >
    {rank}
  </span>
);

/** Figures, not bars: the only share available is "the worst of this team", which is not a quantity. */
const DeviationRow = ({ row, rank }: { row: Deviation; rank: number }): JSX.Element => {
  const { t } = useTranslation();
  // Billed less budgeted, as the Compare view this card opens reads it; the data keeps budgeted less reported.
  const hours = -row.hoursLeft;
  const cost = row.moneyLeft === null ? null : -row.moneyLeft;
  const money = cost === null ? t('insights.offPlan.noRate') : signedEur(cost);

  return (
    <tr>
      <td className={`${planCell} text-left`}>
        <RankChip rank={rank} />
      </td>
      <th scope="row" className={`${planCell} text-left`}>
        <span className="block truncate text-sm font-medium text-foreground max-md:text-xs" title={row.name}>
          {row.name}
        </span>
      </th>
      <td className={`${planCell} text-right`}>
        {/* One colour class, not one over another: two `color` utilities are settled by emit order. */}
        <span className="flex flex-col items-end whitespace-nowrap leading-tight tabular-nums">
          <span className={cx('text-base font-semibold max-md:text-sm', gapClass(hours))}>
            {t('budget.hoursValue', { hours: signedHours(hours) })}
          </span>
          {/* Coloured on the money, not the hours above it, so "No rate" stays grey. */}
          {hours !== 0 && <span className={cx('text-xs max-md:text-[0.6875rem]', gapClass(cost))}>{money}</span>}
        </span>
      </td>
    </tr>
  );
};

export default DeviationRow;
